import { useQuery } from '@tanstack/react-query';
import { jiraApi } from '../services/api';

// Test Jira connection
export const useJiraConnection = () => {
  return useQuery({
    queryKey: ['jiraConnection'],
    queryFn: () => jiraApi.testConnection(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

// Get teams configuration
export const useTeamsConfig = () => {
  return useQuery({
    queryKey: ['jiraTeamsConfig'],
    queryFn: () => jiraApi.getTeamsConfig(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get executive dashboard
export const useExecutiveDashboard = (filters = {}) => {
  return useQuery({
    queryKey: ['jiraExecutiveDashboard', filters.startDate, filters.endDate],
    queryFn: () => jiraApi.getExecutiveDashboard(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Get sprint team metrics
export const useSprintTeamMetrics = (teamKey, filters = {}) => {
  return useQuery({
    queryKey: ['jiraSprintTeam', teamKey, filters.startDate, filters.endDate, filters.sprintIds],
    queryFn: () => jiraApi.getSprintTeamMetrics(teamKey, filters),
    enabled: !!teamKey,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Get kanban team metrics
export const useKanbanTeamMetrics = (teamKey, filters = {}) => {
  return useQuery({
    queryKey: ['jiraKanbanTeam', teamKey, filters.startDate, filters.endDate],
    queryFn: () => jiraApi.getKanbanTeamMetrics(teamKey, filters),
    enabled: !!teamKey,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Get board sprints
export const useBoardSprints = (boardId, state = 'active,closed') => {
  return useQuery({
    queryKey: ['jiraBoardSprints', boardId, state],
    queryFn: () => jiraApi.getBoardSprints(boardId, state),
    enabled: !!boardId,
    staleTime: 5 * 60 * 1000,
  });
};

// Get sprint issues
export const useSprintIssues = (sprintId) => {
  return useQuery({
    queryKey: ['jiraSprintIssues', sprintId],
    queryFn: () => jiraApi.getSprintIssues(sprintId),
    enabled: !!sprintId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Get board issues (for Kanban)
export const useBoardIssues = (boardId, jql = '') => {
  return useQuery({
    queryKey: ['jiraBoardIssues', boardId, jql],
    queryFn: () => jiraApi.getBoardIssues(boardId, jql),
    enabled: !!boardId,
    staleTime: 2 * 60 * 1000,
  });
};

// Get velocity chart
export const useVelocityChart = (boardId) => {
  return useQuery({
    queryKey: ['jiraVelocityChart', boardId],
    queryFn: () => jiraApi.getVelocityChart(boardId),
    enabled: !!boardId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Search issues with JQL
export const useJiraSearch = (jql, fields, enabled = true) => {
  return useQuery({
    queryKey: ['jiraSearch', jql, fields],
    queryFn: () => jiraApi.searchIssues(jql, fields),
    enabled: !!jql && enabled,
    staleTime: 2 * 60 * 1000,
  });
};

// Get issue patterns analysis
export const useIssuePatterns = (teamKey, options = {}, enabled = false) => {
  return useQuery({
    queryKey: ['jiraIssuePatterns', teamKey, options.startDate, options.endDate],
    queryFn: () => jiraApi.getIssuePatterns(teamKey, options),
    enabled: !!teamKey && enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes - pattern analysis is expensive
    refetchOnWindowFocus: false,
  });
};
